import { T, SERIES, PIPELINE_COLORS, ACCENT_NESTED } from "utils/backrrTokens";

// The three directions are drawn in BACKRR'S OWN palette, not the design
// canvas's. The canvas explored a violet of its own (#4C1DD6) on a cream
// ground; carrying that into the app would have given the dashboard a second
// brand, and the Midnight direction would have introduced a dark mode this
// product does not have. So the canvas supplies the LAYOUTS and the motion,
// and every colour here resolves to a token the rest of the admin already uses.
//
// What distinguishes A, B and C is therefore structure and chart form — card
// grid vs dense console vs editorial band; bars vs lines vs heat cells — which
// is the part of a direction worth comparing anyway.
export const PALETTE = {
  ground: T.pageBg,
  surface: T.surface,
  line: T.border,
  track: T.grid,
  ink: T.ink,
  inkSoft: T.inkMuted,
  inkMuted: T.inkSubtle,
  accent: T.accent,
  accentSoft: T.accentSoft,
  accentNested: ACCENT_NESTED,
  iconChip: T.iconChip,
  series: SERIES,
  pipeline: PIPELINE_COLORS,
  up: { bg: T.upBg, fg: T.upText },
  down: { bg: T.downBg, fg: T.downText },
  flat: { bg: T.flatBg, fg: T.flat },
};

// One spacing scale for all three, matching the shipped dashboard so switching
// variants never changes the page's rhythm: 4 / 8 / 12 / 16 / 24, card padding
// 14x16, radius 12. Nothing here is a variant's choice to make.
export const SPACE = {
  xs: 0.5, // 4
  sm: 1, // 8
  md: 1.5, // 12
  lg: 2, // 16
  xl: 3, // 24
};

export const CARD = {
  background: T.surface,
  borderRadius: T.radius,
  border: `1px solid ${T.border}`,
  padding: "14px 16px",
};

// Figures are tabular so columns of numbers line up, in the app's own type —
// the canvas's Geist Mono would have been a second typeface for one purpose.
export const NUM = { fontVariantNumeric: "tabular-nums" };

export const TYPE = {
  h1: "1.25rem", // the page title, matching the shipped header
  section: "0.875rem",
  label: "0.78rem",
  meta: "0.75rem",
  micro: "0.72rem",
};

// A row of tiles (cards inside a widget) that wraps responsively, balanced
// and without holes. Given how many tiles are shown, each width band of the
// row picks the fewest rows that fit and spreads the tiles evenly across them:
// 4 tiles that fit 3-across go 2+2, 6 that fit 4-across go 3+3, 5 go 3+2 with
// the last row stretched. Container queries, so it follows the card's own
// width (sidebar, density, half/full-width widget), not the viewport. Tiles
// in a row are equal height.
//
// `item` selects the tiles; the default is the WidgetGate (Collapse) roots
// DragGrid renders inside its display:contents wrapper.
const DRAG_TILE = "& > div > .MuiCollapse-root";

export const tileRow = (min, gap, count, item = DRAG_TILE) => {
  const g = gap * 8; // MUI spacing units -> px
  const basis = (cols) => (cols <= 1 ? "100%" : `calc((100% - ${(cols - 1) * g}px) / ${cols})`);
  const balanced = (fit) => Math.ceil(count / Math.ceil(count / fit));
  const sx = {
    containerType: "inline-size",
    display: "flex",
    flexWrap: "wrap",
    gap,
    // border-box, so a padded tile's basis is its full width and N tiles fit N across.
    [item]: { flex: `1 1 ${basis(1)}`, minWidth: 0, boxSizing: "border-box" },
    // WidgetGate's fill sets height:100%, which would stop a flex row stretching.
    [`${item}.MuiCollapse-entered`]: { height: "auto" },
  };
  for (let fit = 2; fit <= Math.max(count, 1); fit++) {
    sx[`@container (min-width: ${fit * min + (fit - 1) * g}px)`] = { [item]: { flexBasis: basis(balanced(fit)) } };
  }
  return sx;
};
